/* eslint-disable @typescript-eslint/no-redundant-type-constituents, @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-invalid-void-type, @typescript-eslint/no-floating-promises, @typescript-eslint/no-unnecessary-type-conversion -- API slice boundary: legacy invite/account response unions and fire-and-forget invalidations. */
import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryReturnValue,
} from '@reduxjs/toolkit/query';
import { hasRecords } from '@/utils/apiPayload';
import { waPlatformQuery, waServicesQuery } from './baseQueries';
import type { WARootState } from './store';
import { advisorInvestorsApi } from './advisorInvestorsApi';
import { accountsApi } from './accountsApi';
import type { RecordResponse } from '@/models/ServiceTypes';

export interface InvestorInviteSetting {
  UseLastName: boolean;
  AdditionalMessage?: string;
  CustomSignature?: string;
  UseGroupName: boolean;
  AdvisorGroupName: string;
  AdvisorName: string;
  LoggedInUserName?: string;
  Content?: string;
}

export interface CreateSetupInvestorRequest {
  FirstName: string;
  LastName?: string | null | undefined;
  UserName: string;
  SocialSecurityNumber?: string | null | undefined;
  DOB?: string | null | undefined;
  State?: string | null | undefined;
  Gender?: number | null | undefined;
  MaritalStatus?: number | null | undefined;
  Name: string;
  TDAceUserId?: string | null;
  ExternalId?: string | null;
  IsProspect?: boolean;
}

interface CreateSetupInvestorResponse extends CreateSetupInvestorRequest {
  InvestorId: number;
  UserId: number;
  CreateDate: Date; // "2026-07-10T00:00:00";
  OnboardingStep: number; // TODO: map to enum
  OnboardingStepDisplay: string; // "Not Invited"
  CanInvite: boolean; // true
  CanImpersonate: boolean; // true
  CanDelete: boolean; // true
  CanAssign: boolean; // true
  IsAdminLocked: boolean; // false;
  SocialSecurityNumber: string; // TODO: https://wealthaccess.atlassian.net/browse/IU-275
}

// Multiple invite templates
export interface MultipleInviteTemplateItem {
  MultipleInviteId: number | string;
  InviteName?: string | null;
  Content?: string | null;
}

export type MultipleInvitesForInvestorResponse =
  | MultipleInviteTemplateItem
  | { Records: MultipleInviteTemplateItem[] };

export type InvestorMultipleInviteSettingResponse =
  | MultipleInviteTemplateItem[]
  | { Records: MultipleInviteTemplateItem[] };

export interface BulkInviteRequest {
  UserIds: number[];
  UseLastName: boolean;
  signature: string;
  InvitorName: string; // If "Custom", server expects OtherName; we normalize before sending
  OtherName?: string;
  CCAdvisor?: boolean;
  // Legacy-compatible optional fields (kept optional for flexibility)
  AdditionalMessage?: string;
  AdditionalEmails?: string;
  MultipleInviteId?: number | string | null;
  InviteName?: string | null;
  Content?: string;
  UseGroupName?: boolean;
  CustomSignature?: string;
  IsActive?: boolean;
  AdvisorId?: number | string | null;
  AdvisorGroupId?: number | string | null;
  AdvisorName?: string;
  AdvisorGroupName?: string;
  LoggedInUserName?: string | null;
  IncludeScheduleAssistance?: boolean;
}

export type BulkInviteSkipReason = 'AlreadyActive' | 'InvalidEmail' | 'Failed';

export interface BulkInviteInvestorRef {
  UserId: number;
  InvestorId: number;
  InvestorName?: string;
}

export interface BulkInviteSkippedInvestor extends BulkInviteInvestorRef {
  Reason: BulkInviteSkipReason;
}

export interface BulkInviteResponse {
  NumberInvited: number;
  NumberSuccessfullyInvited: number;
  InvestorsWithInvalidEmails?: BulkInviteInvestorRef[];
  SkippedInvestors?: BulkInviteSkippedInvestor[];
}

// Payload for inviting a single investor (parity with legacy InviteInvestor.jsx)
export interface InviteInvestorRequest {
  CCAdvisor?: boolean;
  Salutation?: string;
  signature?: string;
  InvitorName?: string;
  OtherName?: string;
  MultipleInviteId?: number | string | null;
  InviteName?: string | null;
  Content?: string;
  UseGroupName?: boolean;
  UseLastName?: boolean;
  CustomSignature?: string;
  IsActive?: boolean;
  AdvisorId?: number | string | null;
  AdvisorGroupId?: number | string | null;
  AdvisorName?: string;
  AdvisorGroupName?: string;
  LoggedInUserName?: string | null;
  IncludeScheduleAssistance?: boolean;
  AdvisorPermissions?: unknown | null;
  AdditionalMessage?: string;
  AdditionalEmails?: string;
  // Common investor fields merged from the component (do not compute here)
  InvestorId?: number | string;
  UserId?: number | string;
  FirstName?: string;
  LastName?: string;
  Name?: string;
  UserName?: string;
  CreateDate?: string;
  OnboardingStep?: number;
  OnboardingStepDisplay?: string;
  CanInvite?: boolean;
  CanImpersonate?: boolean;
  CanDelete?: boolean;
  CanAssign?: boolean;
  IsAdminLocked?: boolean;
  SocialSecurityNumber?: string;
  salutation?: string;
}

export interface UpdateInviteSettingRequest {
  IsActive: boolean;
  AdvisorGroupEmailIsActive: boolean;
  MultipleInviteId: string | number | null;
  InviteName: string | null;
  Content: string;
  UseGroupName: boolean;
  UseLastName: boolean;
  CustomSignature: string;
  AdvisorId: number | string | null;
  AdvisorGroupId: number | string | null;
  AdvisorName: string;
  AdvisorGroupName: string;
  LoggedInUserName: string | null;
  IncludeScheduleAssistance: boolean;
}

type BaseQueryResult<T> = QueryReturnValue<
  T,
  FetchBaseQueryError,
  FetchBaseQueryMeta
>;

type AdvisorAccountSummary = {
  WealthAccountId: number;
  Name: string;
  AccountNumber?: string;
  Balance?: number;
  Investors?: { InvestorId: number; InvestorName: string }[];
};

export interface AdvisorInvestorGroupRecord {
  AdvisorNamedInvestorGroupId: number;
  Name: string;
  GroupName?: string;
  ParentId?: number;
}

const isAdvisorAccountSummaryArray = (
  value: unknown,
): value is AdvisorAccountSummary[] => {
  return Array.isArray(value);
};

export const clientsApi = createApi({
  reducerPath: 'clientsApi',
  baseQuery: waServicesQuery,
  tagTypes: [
    'InviteSettings',
    'AdvisorSetupInvestors',
    'InvestorsOnboardingChart',
  ],
  endpoints: (builder) => ({
    // --- Create a new setup investor (parity with legacy Forms/Investor.jsx create) ---
    createSetupInvestor: builder.mutation<
      CreateSetupInvestorResponse,
      CreateSetupInvestorRequest
    >({
      queryFn: async (body, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const url = `/api/${currentRole}/setup/investors`;

        const result = (await baseQuery({
          url,
          method: 'POST',
          body,
        })) as BaseQueryResult<CreateSetupInvestorResponse>;
        if (result.error) return { error: result.error };
        return {
          data: result.data || {},
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // swallow
        } finally {
          // After creating an investor, mirror legacy behavior by refreshing related resources
          try {
            // Do NOT force refetch the main investors grid here to avoid an extra request with default params.
            // Invalidate only setup investors and onboarding chart; the component can trigger any specific refresh it needs.
            type InvalidateAdvisorTagsArg = Parameters<
              typeof advisorInvestorsApi.util.invalidateTags
            >[0];
            dispatch(
              advisorInvestorsApi.util.invalidateTags([
                { type: 'AdvisorSetupInvestors', id: 'LIST' },
                { type: 'InvestorsOnboardingChart', id: 'LIST' },
                ...(arg.IsProspect
                  ? [{ type: 'AdvisorProspects', id: 'LIST' }]
                  : []),
              ] as InvalidateAdvisorTagsArg),
            );
          } catch {
            /* non-critical */
          }
        }
      },
    }),

    // --- Invite a single investor (parity with legacy inviteInvestors store) ---
    inviteInvestor: builder.mutation<
      unknown,
      { userId: number | string; body: InviteInvestorRequest }
    >({
      queryFn: async ({ userId, body }, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const id = encodeURIComponent(String(userId));
        const result = (await baseQuery({
          url: `/api/${currentRole}/setup/investors/${id}/invite`,
          method: 'POST',
          body,
        })) as BaseQueryResult<unknown>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // swallow
        } finally {
          // Invalidate key advisor investors caches to mirror legacy affected stores
          try {
            type InvalidateAdvisorTagsArg = Parameters<
              typeof advisorInvestorsApi.util.invalidateTags
            >[0];
            dispatch(
              advisorInvestorsApi.util.invalidateTags([
                { type: 'AdvisorInvestorsList', id: 'LIST' },
                { type: 'AdvisorSetupInvestors', id: 'LIST' },
                { type: 'InvestorsOnboardingChart', id: 'LIST' },
                { type: 'AdvisorProspects', id: 'LIST' },
              ] as InvalidateAdvisorTagsArg),
            );
          } catch {
            /* non-critical */
          }
        }
      },
    }),
    getInvestorInviteSetting: builder.query<
      InvestorInviteSetting,
      { userId?: number | string } | void
    >({
      queryFn: async (arg, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const url = `/api/${currentRole}/investorInviteSetting`;
        const params = arg && arg?.userId ? { userId: arg.userId } : undefined;
        const result = (await baseQuery({
          url,
          method: 'GET',
          ...(params ? { params } : {}),
        })) as BaseQueryResult<InvestorInviteSetting>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
      providesTags: ['InviteSettings'],
    }),

    // --- Investor Groups (for Client Group dropdowns) ---
    getAdvisorInvestorGroups: builder.query<
      RecordResponse<AdvisorInvestorGroupRecord>,
      void
    >({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const result = (await baseQuery({
          url: `/api/advisor/InvestorGroups/`,
          method: 'GET',
          params: {
            'sort[0].Property': 'Name',
            'sort[0].Direction': 'asc',
            limit: 100,
          },
        })) as BaseQueryResult<RecordResponse<AdvisorInvestorGroupRecord>>;
        if (result.error) return { error: result.error };
        return {
          data: result.data,
        };
      },
      // Tagged so the client-group management flows (rename/add/remove a group
      // or segment) can invalidate this cache; without it the Opportunities
      // group dropdown keeps serving stale names until reload.
      providesTags: [{ type: 'AdvisorInvestorGroups', id: 'LIST' }],
    }),

    // Force AdvisorGroup and Force Advisor settings (used by Edit Invitation modal)
    getInvestorInviteSettingForceAdvisorGroup: builder.query<
      InvestorInviteSetting,
      void
    >({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const url = `/api/${currentRole}/investorInviteSetting?forceAdvisorGroup=true`;
        const result = (await baseQuery({
          url,
          method: 'GET',
        })) as BaseQueryResult<InvestorInviteSetting>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
    }),
    getInvestorInviteSettingForceAdvisor: builder.query<
      InvestorInviteSetting,
      void
    >({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const url = `/api/${currentRole}/investorInviteSetting?forceAdvisor=true`;
        const result = (await baseQuery({
          url,
          method: 'GET',
        })) as BaseQueryResult<InvestorInviteSetting>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
    }),

    // Multiple invites endpoints for a specific investor
    getInvestorMultipleInviteSetting: builder.query<
      InvestorMultipleInviteSettingResponse,
      number
    >({
      queryFn: async (investorId, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const url = `/api/${currentRole}/investorMultipleInviteSetting?investorId=${encodeURIComponent(String(investorId))}`;
        const result = (await baseQuery({
          url,
          method: 'GET',
        })) as BaseQueryResult<InvestorMultipleInviteSettingResponse>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
    }),
    getMultipleInvitesForInvestor: builder.query<
      MultipleInvitesForInvestorResponse,
      number
    >({
      queryFn: async (investorId, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const url = `/api/${currentRole}/investorInviteSetting/multipleInvites/investorId/${encodeURIComponent(String(investorId))}`;
        const result = (await baseQuery({
          url,
          method: 'GET',
        })) as BaseQueryResult<MultipleInvitesForInvestorResponse>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
    }),

    // Update current invite setting (toggle advisor vs group and base template content)
    updateInvestorInviteSetting: builder.mutation<
      InvestorInviteSetting,
      Partial<UpdateInviteSettingRequest>
    >({
      queryFn: async (body, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const url = `/api/${currentRole}/investorInviteSetting`;
        const result = (await baseQuery({
          url,
          method: 'PUT',
          body,
        })) as BaseQueryResult<InvestorInviteSetting>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
      invalidatesTags: ['InviteSettings'],
    }),

    // Select a specific MultipleInvite template for the advisor (parity with legacy PUT /investorInviteSetting/multipleInvites/{id})
    updateSelectedMultipleInvite: builder.mutation<
      boolean,
      { multipleInviteId: number | string }
    >({
      queryFn: async (
        { multipleInviteId },
        { getState },
        _extra,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        // Prefer dedicated endpoint when available (advisor role)
        if (currentRole === 'advisor') {
          const url = `/api/${currentRole}/investorInviteSetting/multipleInvites/${encodeURIComponent(String(multipleInviteId))}`;
          const result = (await baseQuery({
            url,
            method: 'PUT',
          })) as BaseQueryResult<unknown>;
          if (result.error) return { error: result.error };
          // We don't use the response payload in the UI; expose success as boolean
          return { data: true };
        }
        // Fallback for other roles: update via generic endpoint with MultipleInviteId
        const result = (await baseQuery({
          url: `/api/${currentRole}/investorInviteSetting`,
          method: 'PUT',
          body: { MultipleInviteId: multipleInviteId },
        })) as BaseQueryResult<unknown>;
        if (result.error) return { error: result.error };
        return { data: true };
      },
      invalidatesTags: ['InviteSettings'],
    }),

    bulkInviteInvestors: builder.mutation<
      BulkInviteResponse,
      BulkInviteRequest
    >({
      queryFn: async (body, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const normalized: BulkInviteRequest = {
          ...body,
          InvitorName:
            body.InvitorName === 'Custom' && body.OtherName
              ? body.OtherName
              : body.InvitorName,
        };
        const result = (await baseQuery({
          url: `/api/${currentRole}/investors/bulkInvite`,
          method: 'POST',
          body: normalized,
        })) as BaseQueryResult<BulkInviteResponse>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          type InvalidateAdvisorTagsArg = Parameters<
            typeof advisorInvestorsApi.util.invalidateTags
          >[0];
          dispatch(
            advisorInvestorsApi.util.invalidateTags([
              { type: 'AdvisorProspects', id: 'LIST' },
            ] as InvalidateAdvisorTagsArg),
          );
        } catch {
          /* the caller reports the failure */
        }
      },
    }),

    // --- Upload investors file (parity with legacy UploadFile.jsx) ---
    uploadInvestorsFile: builder.mutation<
      unknown,
      { file: File; filename?: string }
    >({
      queryFn: async ({ file, filename }, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const formData = new FormData();
        const effectiveName = filename ?? file.name;
        formData.append('filename', effectiveName);
        formData.append('file', file);
        const result = (await baseQuery({
          url: `/api/${currentRole}/Investors/upload/`,
          method: 'POST',
          body: formData,
        })) as BaseQueryResult<unknown>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
    }),

    // --- Update a single uploaded investor row (legacy parity: PUT /Investors/upload/{id}) ---
    updateUploadedInvestor: builder.mutation<
      unknown,
      {
        UploadInvestorModelId: number | string;
        FirstName?: string;
        LastName?: string;
        UserName?: string;
        ErrorMessage?: string;
        IsValid?: boolean;
      }
    >({
      queryFn: async (body, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const id = encodeURIComponent(String(body.UploadInvestorModelId));
        const result = (await baseQuery({
          url: `/api/${currentRole}/Investors/upload/${id}`,
          method: 'PUT',
          body,
        })) as BaseQueryResult<unknown>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
    }),

    // --- Bulk insert uploaded investors (legacy parity: /api/advisor/investors/bulkInsert) ---
    bulkInsertInvestors: builder.mutation<
      unknown,
      {
        investors: {
          UploadInvestorModelId?: number | string;
          FirstName?: string;
          LastName?: string;
          UserName?: string;
          ErrorMessage?: string;
          IsValid?: boolean;
        }[];
      }
    >({
      queryFn: async ({ investors }, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const result = (await baseQuery({
          url: `/api/${currentRole}/investors/bulkInsert`,
          method: 'POST',
          body: { investors },
        })) as BaseQueryResult<unknown>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // swallow
        } finally {
          // Mirror legacy follow-up requests after bulk insert
          try {
            // Invalidate caches
            type InvalidateAdvisorTagsArg = Parameters<
              typeof advisorInvestorsApi.util.invalidateTags
            >[0];
            dispatch(
              advisorInvestorsApi.util.invalidateTags([
                { type: 'AdvisorInvestorsList', id: 'LIST' },
                { type: 'AdvisorSetupInvestors', id: 'LIST' },
                { type: 'InvestorsOnboardingChart', id: 'LIST' },
              ] as InvalidateAdvisorTagsArg),
            );
            // Actively refetch key endpoints to update UI immediately
            dispatch(
              advisorInvestorsApi.endpoints.getAdvisorInvestors.initiate(
                {},
                { forceRefetch: true },
              ),
            );
            dispatch(
              advisorInvestorsApi.endpoints.getAdvisorSetupInvestors.initiate(
                undefined,
                { forceRefetch: true },
              ),
            );
            dispatch(
              advisorInvestorsApi.endpoints.getInvestorsOnboardingChart.initiate(
                undefined,
                { forceRefetch: true },
              ),
            );
          } catch {
            /* intentionally left blank: non-critical refetch failures are safe to ignore */
          }
        }
      },
    }),

    // --- Manage Client Permissions (parity with legacy manageClientPermissions store) ---
    getManageClientPermissions: builder.query<
      {
        CanAddClients?: boolean;
        CanCreateHouseholds?: boolean;
        HouseholdManager?: string | null;
        NumberOfImportIssues?: number;
        NumberOfPossibleDuplicates?: number;
      },
      void
    >({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        type ManageClientPermissions = {
          CanAddClients?: boolean;
          CanCreateHouseholds?: boolean;
          HouseholdManager?: string | null;
          NumberOfImportIssues?: number;
          NumberOfPossibleDuplicates?: number;
        };
        const result = (await baseQuery({
          url: `/api/advisor/ManageClientPermissions`,
          method: 'GET',
        })) as BaseQueryResult<ManageClientPermissions>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
    }),

    // --- Accounts list for selected investors (legacy: /api/advisor/accounts with investorIds) ---
    getAdvisorAccountsForInvestors: builder.query<
      AdvisorAccountSummary[],
      { investorIds: number[] }
    >({
      queryFn: async ({ investorIds }, _api, _extra, baseQuery) => {
        const params: Record<string, string> = {
          investorIds: investorIds.join(','),
          showAway: 'false',
          ExcludedAccountTypes: '1,2,3,4',
          _: Date.now().toString(),
        };
        const result = (await baseQuery({
          url: `/api/advisor/accounts`,
          method: 'GET',
          params,
        })) as BaseQueryResult<unknown>;
        if (result.error) return { error: result.error };
        let payload: unknown = result.data;
        if (hasRecords(payload)) {
          payload = payload.Records;
        }
        const data: AdvisorAccountSummary[] = isAdvisorAccountSummaryArray(
          payload,
        )
          ? payload
          : [];
        return { data };
      },
    }),

    // --- Create Household (parity with legacy households store .create) ---
    createHousehold: builder.mutation<
      unknown,
      {
        Name: string;
        Investors: { InvestorId: number; Name?: string }[];
        PrimaryInvestorId?: number | null;
        SecondaryInvestorId?: number | null;
        DisplaySetting?: number | string | null; // 1,2,3
        // Legacy sometimes includes lowercase duplicates
        primaryInvestorId?: number | null;
        secondaryInvestorId?: number | null;
        ExcludedAccounts?: number[]; // When choosing subset
      }
    >({
      queryFn: async (body, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole || 'advisor';
        const result = (await baseQuery({
          url: `/api/${currentRole}/households`,
          method: 'POST',
          body,
        })) as BaseQueryResult<unknown>;
        if (result.error) return { error: result.error };
        return { data: result.data };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // swallow
        } finally {
          // Cross-slice cache refresh to mirror legacy affectedStores
          type InvalidateAdvisorTagsArg = Parameters<
            typeof advisorInvestorsApi.util.invalidateTags
          >[0];
          dispatch(
            advisorInvestorsApi.util.invalidateTags([
              { type: 'AdvisorInvestorsList', id: 'LIST' },
              { type: 'AdvisorSetupInvestors', id: 'LIST' },
              { type: 'InvestorsOnboardingChart', id: 'LIST' },
            ] as InvalidateAdvisorTagsArg),
          );
          type InvalidateAccountsTagsArg = Parameters<
            typeof accountsApi.util.invalidateTags
          >[0];
          dispatch(
            accountsApi.util.invalidateTags([
              { type: 'Accounts', id: 'LIST' },
              'AccountsHistorical',
              { type: 'Holdings', id: 'LIST' },
            ] as InvalidateAccountsTagsArg),
          );
          // Fire follow-up GET: accounts for the investors just included in the household
          try {
            const investorIds = Array.isArray(arg?.Investors)
              ? (arg.Investors as { InvestorId: number }[])
                  .map((i) => Number(i.InvestorId))
                  .filter((id) => !Number.isNaN(id))
              : [];
            if (investorIds.length > 0) {
              dispatch(
                clientsApi.endpoints.getAdvisorAccountsForInvestors.initiate(
                  { investorIds },
                  { forceRefetch: true },
                ),
              );
            }
            // Legacy also reloads investors list with ExcludedOnboardingSteps=2,3
            dispatch(
              advisorInvestorsApi.endpoints.getAdvisorInvestors.initiate(
                { excludedOnboardingSteps: '2,3' },
                { forceRefetch: true },
              ),
            );
          } catch {
            /* intentionally left blank: non-critical refetch failures are safe to ignore */
          }
        }
      },
    }),

    // Note: bulkDeleteInvestors lives in advisorInvestorsApi to leverage tag invalidation

    // Returns raw HTML for the invitation preview (platform endpoint)
    getPreviewHtml: builder.query<
      string,
      {
        investorId: number;
        investorName: string;
        options: Partial<BulkInviteRequest> & {
          Salutation?: string;
          AdvisorId?: number | string;
          AdvisorGroupId?: number | string;
          AdvisorName?: string;
          AdvisorGroupName?: string;
          LoggedInUserName?: string;
          UseGroupName?: boolean;
          CustomSignature?: string;
          Content?: string;
          IncludeScheduleAssistance?: boolean;
          IsActive?: boolean;
          MultipleInviteId?: string | number | null;
          InviteName?: string | null;
          InvestorId?: number | string; // legacy expects both cases sometimes
        };
      }
    >({
      queryFn: async (
        { investorId, investorName, options },
        api,
        extraOptions,
      ) => {
        const state = api.getState() as WARootState;
        const role = state.auth.currentRole || 'advisor';

        const params = new URLSearchParams();
        params.set('investorId', String(investorId));
        params.set('investorName', investorName);
        params.set('role', role);
        if (typeof options.UseLastName === 'boolean')
          params.set('UseLastName', String(options.UseLastName));
        if (options.AdditionalMessage)
          params.set('AdditionalMessage', options.AdditionalMessage);
        if (options.signature) params.set('signature', options.signature);
        if (options.InvitorName) params.set('InvitorName', options.InvitorName);
        if (options.Salutation) params.set('Salutation', options.Salutation);
        if (state.appView?.UserInfo?.UserId && !options.AdvisorId) {
          params.set('advisorUserId', String(state.appView.UserInfo.UserId));
        }
        // Legacy parity extra params when available
        if (options.Content) params.set('Content', options.Content);
        if (typeof options.UseGroupName === 'boolean')
          params.set('UseGroupName', String(options.UseGroupName));
        if (options.CustomSignature)
          params.set('CustomSignature', options.CustomSignature);
        if (typeof options.IsActive === 'boolean')
          params.set('IsActive', String(options.IsActive));
        if (options.AdvisorId !== undefined)
          params.set('AdvisorId', String(options.AdvisorId ?? ''));
        if (options.AdvisorGroupId !== undefined)
          params.set('AdvisorGroupId', String(options.AdvisorGroupId ?? ''));
        if (options.AdvisorName) params.set('AdvisorName', options.AdvisorName);
        if (options.AdvisorGroupName)
          params.set('AdvisorGroupName', options.AdvisorGroupName);
        if (options.LoggedInUserName)
          params.set('LoggedInUserName', options.LoggedInUserName);
        if (typeof options.IncludeScheduleAssistance === 'boolean')
          params.set(
            'IncludeScheduleAssistance',
            String(options.IncludeScheduleAssistance),
          );
        if (options.MultipleInviteId !== undefined)
          params.set(
            'MultipleInviteId',
            String(options.MultipleInviteId ?? ''),
          );
        if (options.InviteName !== undefined && options.InviteName !== null)
          params.set('InviteName', String(options.InviteName));
        if (options.InvestorId !== undefined)
          params.set('InvestorId', String(options.InvestorId));

        const result = (await waPlatformQuery(
          {
            url: `/Advisor/_Preview_Invitation`,
            method: 'POST',
            body: params,
          },
          api,
          extraOptions,
        )) as BaseQueryResult<string>;

        if (result.error) return { error: result.error };
        return { data: result.data };
      },
    }),
  }),
});

export const {
  useGetInvestorInviteSettingQuery,
  useGetInvestorInviteSettingForceAdvisorGroupQuery,
  useGetInvestorInviteSettingForceAdvisorQuery,
  useGetInvestorMultipleInviteSettingQuery,
  useGetMultipleInvitesForInvestorQuery,
  useBulkInviteInvestorsMutation,
  useLazyGetPreviewHtmlQuery,
  useUpdateInvestorInviteSettingMutation,
  useUpdateSelectedMultipleInviteMutation,
  useGetManageClientPermissionsQuery,
  useGetAdvisorAccountsForInvestorsQuery,
  useCreateHouseholdMutation,
  useUploadInvestorsFileMutation,
  useBulkInsertInvestorsMutation,
  useUpdateUploadedInvestorMutation,
  useGetAdvisorInvestorGroupsQuery,
  useCreateSetupInvestorMutation,
  useInviteInvestorMutation,
} = clientsApi;
